/**
 * State for one agent conversation, folded from the SSE event stream.
 *
 * Kept as a pure reducer so the whole event contract can be tested without a
 * DOM, a network or a running backend: feed it a recorded event sequence and
 * assert on the resulting state.
 *
 * The ordering guarantee the backend makes (docs/plan/03-chat-poc.md section
 * 9.5) is that a `sources` event for a batch arrives before any `token` that
 * could cite it, so `citations` is always populated by the time a marker needs
 * rendering. This reducer does not re-check that; it simply relies on it.
 */
import type {
  AgentEvent,
  Citation,
  EvArtifact,
  EvGuardrail,
  EvDone,
  EvToolCall,
  EvToolResult,
} from "../../types/agent-chat";

/** A tool invocation and, once it returns, its outcome. */
export interface ToolStepState {
  toolCallId: string;
  name: string;
  iteration: number;
  arguments: Record<string, unknown>;
  status: EvToolResult["status"] | "running";
  summary?: string;
  durationMs?: number;
}

export interface AgentMessageState {
  /** Client-side id; the server id arrives with `done`. */
  key: string;
  role: "user" | "assistant";
  text: string;
  /** Assistant only: tool steps for this turn, in call order. */
  steps: ToolStepState[];
  /** Assistant only: rendered artifacts produced by generation tools. */
  artifacts: EvArtifact[];
  /** Assistant only: ids actually cited, once the turn completes. */
  citedIds: string[];
  /** Assistant only: retrieved but never cited. */
  unusedIds: string[];
  /** True while tokens are still arriving. */
  isStreaming: boolean;
  /** Set from `done`; enables the feedback control. */
  generationId?: string;
  messageId?: string;
  isRefusal?: boolean;
  /** Historical messages are rendered without the typewriter effect. */
  isHistorical?: boolean;
  /** Assistant only: the model that wrote (or is writing) the turn. */
  model?: string;
}

export interface AgentState {
  conversationId: string | null;
  messages: AgentMessageState[];
  /** Every citation seen this conversation, keyed by its short id. */
  citations: Record<string, Citation>;
  /** Model resolved for the current turn, from `meta`. */
  model: string | null;
  /** Tool names the server exposed for the turn. */
  tools: string[];
  isStreaming: boolean;
  error: { code: string; message: string; retryable: boolean } | null;
  /** Verdict of the pre-call guardrails for the current turn, if they saw anything. */
  guardrail: EvGuardrail | null;
  /** Mirrors the quota figure the server reports on `done`. */
  usageRemaining: number | null;
}

export const initialAgentState: AgentState = {
  conversationId: null,
  messages: [],
  citations: {},
  model: null,
  tools: [],
  isStreaming: false,
  error: null,
  guardrail: null,
  usageRemaining: null,
};

export type AgentAction =
  | { type: "reset"; conversationId?: string | null }
  | { type: "setConversationId"; conversationId: string }
  | { type: "sendUserMessage"; text: string; key: string }
  | { type: "beginAssistant"; key: string }
  | { type: "event"; event: AgentEvent }
  | { type: "loadHistory"; messages: AgentMessageState[]; citations: Citation[] }
  | { type: "failTurn"; code: string; message: string; retryable?: boolean };

/** Apply `patch` to the last assistant message, leaving everything else alone. */
function patchAssistant(
  state: AgentState,
  patch: (m: AgentMessageState) => AgentMessageState
): AgentState {
  const index = [...state.messages]
    .reverse()
    .findIndex((m) => m.role === "assistant");
  if (index === -1) return state;
  const realIndex = state.messages.length - 1 - index;
  const messages = state.messages.slice();
  messages[realIndex] = patch(messages[realIndex]);
  return { ...state, messages };
}

function onToolCall(state: AgentState, ev: EvToolCall): AgentState {
  return patchAssistant(state, (m) => ({
    ...m,
    steps: [
      ...m.steps,
      {
        toolCallId: ev.tool_call_id,
        name: ev.name,
        iteration: ev.iteration,
        arguments: ev.arguments,
        status: "running",
      },
    ],
  }));
}

function onToolResult(state: AgentState, ev: EvToolResult): AgentState {
  return patchAssistant(state, (m) => ({
    ...m,
    steps: m.steps.map((s) =>
      s.toolCallId === ev.tool_call_id
        ? { ...s, status: ev.status, summary: ev.summary, durationMs: ev.duration_ms }
        : s
    ),
  }));
}

function onDone(state: AgentState, ev: EvDone): AgentState {
  const next = patchAssistant(state, (m) => ({
    ...m,
    // Prefer the server's validated text: it is what was persisted, with
    // invalid citation markers already stripped or rewritten.
    text: ev.final_text || m.text,
    citedIds: ev.cited_ids,
    unusedIds: ev.unused_ids,
    generationId: ev.generation_id,
    messageId: ev.message_id,
    isRefusal: ev.is_refusal,
    isStreaming: false,
    steps: m.steps.map((s) => (s.status === "running" ? { ...s, status: "ok" } : s)),
  }));
  return {
    ...next,
    isStreaming: false,
    usageRemaining:
      ev.usage_remaining === undefined || ev.usage_remaining === null
        ? next.usageRemaining
        : ev.usage_remaining,
  };
}

export function agentReducer(state: AgentState, action: AgentAction): AgentState {
  switch (action.type) {
    case "reset":
      return {
        ...initialAgentState,
        conversationId: action.conversationId ?? null,
      };

    case "setConversationId":
      return { ...state, conversationId: action.conversationId };

    case "sendUserMessage":
      return {
        ...state,
        error: null,
        guardrail: null,
        messages: [
          ...state.messages,
          {
            key: action.key,
            role: "user",
            text: action.text,
            steps: [],
            artifacts: [],
            citedIds: [],
            unusedIds: [],
            isStreaming: false,
          },
        ],
      };

    case "beginAssistant":
      return {
        ...state,
        isStreaming: true,
        messages: [
          ...state.messages,
          {
            key: action.key,
            role: "assistant",
            text: "",
            steps: [],
            artifacts: [],
            citedIds: [],
            unusedIds: [],
            isStreaming: true,
          },
        ],
      };

    case "loadHistory":
      return {
        ...state,
        messages: action.messages,
        citations: Object.fromEntries(action.citations.map((c) => [c.id, c])),
        isStreaming: false,
        error: null,
        guardrail: null,
      };

    case "failTurn":
      return {
        ...state,
        isStreaming: false,
        error: {
          code: action.code,
          message: action.message,
          retryable: action.retryable ?? false,
        },
        messages: state.messages.map((m) =>
          m.isStreaming ? { ...m, isStreaming: false } : m
        ),
      };

    case "event": {
      const ev = action.event;
      switch (ev.type) {
        case "meta":
          return {
            ...patchAssistant(state, (m) => ({ ...m, model: ev.model })),
            conversationId: ev.conversation_id,
            model: ev.model,
            tools: ev.tools,
            // A verdict belongs to one turn; meta opens the next one.
            guardrail: null,
          };

        case "guardrail":
          return { ...state, guardrail: ev };

        case "tool_call":
          return onToolCall(state, ev);

        case "tool_result":
          return onToolResult(state, ev);

        case "sources": {
          const citations = { ...state.citations };
          for (const c of ev.citations) citations[c.id] = c;
          return { ...state, citations };
        }

        case "token":
          return patchAssistant(state, (m) => ({ ...m, text: m.text + ev.text }));

        case "citation":
          // The marker is already inside the token text; nothing to fold here.
          // Kept explicit so an unhandled event type is a compile error.
          return state;

        case "artifact":
          return patchAssistant(state, (m) => ({
            ...m,
            artifacts: [...m.artifacts, ev],
          }));

        case "done":
          return onDone(state, ev);

        case "error":
          return {
            ...state,
            isStreaming: false,
            error: {
              code: ev.code,
              message: ev.message,
              retryable: ev.retryable,
            },
            messages: state.messages.map((m) =>
              m.isStreaming ? { ...m, isStreaming: false } : m
            ),
          };

        default:
          return state;
      }
    }

    default:
      return state;
  }
}
